/**
 * MileageService - Calculate distances and mileage reimbursement.
 *
 * DISTANCE ACCURACY: uses the Haversine formula — this is STRAIGHT-LINE
 * ("as the crow flies") distance between GPS points, NOT road/driving distance.
 * Road-distance accuracy would require a maps API (Google Distance Matrix /
 * Mapbox). That's a deliberate later decision, pending Little Walkers' written
 * mileage policy — do NOT add a maps vendor here yet.
 */

// Fallback rate used only when a company has no configured rate history.
const DEFAULT_IRS_MILEAGE_RATE = 0.67; // $/mile

export interface Location {
  latitude: number;
  longitude: number;
  address?: string;
}

/**
 * A company mileage-reimbursement rate effective from a given date. Stored as a
 * history on company.settings.mileageRates so reimbursement uses the rate that
 * was in effect on the DATE OF TRAVEL (see resolveRateForDate), not just the
 * latest rate. Update the rate (with a new effective date) when the IRS rate
 * changes rather than overwriting.
 */
export interface MileageRate {
  rate: number;           // dollars per mile
  effectiveDate: string;  // ISO 'YYYY-MM-DD' — applies to travel on/after this date
  note?: string;
}

export interface MileageCalculation {
  distance: number; // in miles
  rate: number; // per mile
  reimbursementAmount: number;
  startLocation: Location;
  endLocation: Location;
}

class MileageService {
  private irsRate: number = DEFAULT_IRS_MILEAGE_RATE;

  /**
   * Set the IRS mileage rate (admin configuration)
   */
  setIRSRate(rate: number): void {
    if (rate <= 0) {
      throw new Error('IRS rate must be greater than 0');
    }
    this.irsRate = rate;
  }

  /**
   * Get current IRS mileage rate
   */
  getIRSRate(): number {
    return this.irsRate;
  }

  /**
   * Resolve the reimbursement rate that was in effect on a given travel date,
   * from a company's rate history. Picks the entry with the latest
   * effectiveDate that is on or before the travel date; falls back to the
   * built-in default if the company has no configured rates.
   */
  resolveRateForDate(rates: MileageRate[] | undefined, travelDate: Date): number {
    if (!rates || rates.length === 0) return this.irsRate;
    const iso = travelDate.toISOString().slice(0, 10); // YYYY-MM-DD
    const applicable = rates
      .filter((r) => r.effectiveDate <= iso)
      .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
    return applicable.length ? applicable[applicable.length - 1].rate : this.irsRate;
  }

  /**
   * Calculate distance between two GPS coordinates using Haversine formula
   * Returns distance in miles
   */
  calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    // Radius of the Earth in miles
    const R = 3958.8;

    // Convert degrees to radians
    const toRad = (deg: number) => (deg * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return Math.round(distance * 100) / 100; // Round to 2 decimal places
  }

  /**
   * Calculate distance between two Location objects
   */
  calculateDistanceBetweenLocations(
    start: Location,
    end: Location
  ): number {
    return this.calculateDistance(
      start.latitude,
      start.longitude,
      end.latitude,
      end.longitude
    );
  }

  /**
   * Calculate mileage reimbursement
   */
  calculateReimbursement(
    start: Location,
    end: Location,
    customRate?: number
  ): MileageCalculation {
    const distance = this.calculateDistanceBetweenLocations(start, end);
    const rate = customRate || this.irsRate;
    const reimbursementAmount = Math.round(distance * rate * 100) / 100;

    return {
      distance,
      rate,
      reimbursementAmount,
      startLocation: start,
      endLocation: end,
    };
  }

  /**
   * Calculate total mileage for a series of locations
   * Useful for calculating daily mileage when RBT visits multiple clients
   */
  calculateTotalMileage(
    locations: Location[],
    customRate?: number
  ): {
    totalDistance: number;
    totalReimbursement: number;
    rate: number;
    segments: MileageCalculation[];
  } {
    if (locations.length < 2) {
      return {
        totalDistance: 0,
        totalReimbursement: 0,
        rate: customRate || this.irsRate,
        segments: [],
      };
    }

    const segments: MileageCalculation[] = [];
    let totalDistance = 0;

    for (let i = 0; i < locations.length - 1; i++) {
      const calculation = this.calculateReimbursement(
        locations[i],
        locations[i + 1],
        customRate
      );
      segments.push(calculation);
      totalDistance += calculation.distance;
    }

    const rate = customRate || this.irsRate;
    const totalReimbursement = Math.round(totalDistance * rate * 100) / 100;

    return {
      totalDistance: Math.round(totalDistance * 100) / 100,
      totalReimbursement,
      rate,
      segments,
    };
  }

  /**
   * Format distance for display
   */
  formatDistance(distance: number): string {
    return `${distance.toFixed(2)} mi`;
  }

  /**
   * Format reimbursement amount for display
   */
  formatReimbursement(amount: number): string {
    return `$${amount.toFixed(2)}`;
  }

  /**
   * Validate GPS coordinates
   */
  isValidCoordinates(latitude: number, longitude: number): boolean {
    return (
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    );
  }

  /**
   * Get address from coordinates (reverse geocoding)
   * Note: This would typically use a service like Google Maps API
   * For now, returns a placeholder
   */
  async reverseGeocode(
    latitude: number,
    longitude: number
  ): Promise<string> {
    // TODO: Implement actual reverse geocoding with Google Maps API or similar
    // For now, return coordinates as string
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  }

  /**
   * Calculate mileage from session locations
   * Extracts GPS coordinates from check-in locations and calculates distance
   */
  calculateSessionMileage(
    checkInLocations: Array<{
      latitude: number;
      longitude: number;
      accuracy?: number;
    }>,
    customRate?: number
  ): {
    totalDistance: number;
    totalReimbursement: number;
    rate: number;
  } {
    if (checkInLocations.length < 2) {
      return {
        totalDistance: 0,
        totalReimbursement: 0,
        rate: customRate || this.irsRate,
      };
    }

    const locations: Location[] = checkInLocations.map(loc => ({
      latitude: loc.latitude,
      longitude: loc.longitude,
    }));

    const result = this.calculateTotalMileage(locations, customRate);

    return {
      totalDistance: result.totalDistance,
      totalReimbursement: result.totalReimbursement,
      rate: result.rate,
    };
  }
}

export const mileageService = new MileageService();
